import React from 'react'
import axios, { AxiosError } from 'axios'
import { notification, Upload, Form } from 'antd'
import { RcFile } from 'antd/lib/upload/interface'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import { Uploads_Insert_Input } from '@organice/graphql'
import { useUploadMutations } from '@organice/utils/hooks/useUploadMutation'

import {
  useServiceDisplayMode,
  ServiceDisplayMode,
  useServiceModulePermissions,
} from '@organice/contexts/serviceDisplay'
import { useMeContext } from '@organice/contexts'

import { useTranslation } from 'react-i18next'
import { convertValueUploadsToFileList } from './converters'
import {
  formatFileName,
  prepareUploaFormItemId,
  sanitizeFileName,
  splitFileNameAndExtension,
} from './file-name'
import { RegularFileEntry, renderAttributeFileEntry } from './file-entry'
import {
  DisplayList,
  DisplayListItem,
  FileList,
  UploadWrapper,
  UploadWrapperHideList,
} from './styles'
import {
  FileAdded,
  FileUploadProps,
  UploadFile,
  UploadFileVersion,
  VersionedValueUpload,
} from './types'
export * from './converters'
export * from './file-entry'
export * from './types'

const UPLOAD_URI = `https://${
  import.meta.env.VITE_FRONTEND_API_HOST || document.location.host
}`

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const FileUploadVersioned: React.FC<FileUploadProps> = ({
  children,
  module,
  attributeGroupId,
  attribute,
  attributeFiles,
  roomAttributeFiles,
  attributeId,
  id,
  name,
  fieldName,
  fileList,
  isConfiguration,
  disableUpload,
  onAdded,
  onProgress,
  onComplete,
  onChanged,
  onUploadNewVersion,
  onDeleteVersion,
}) => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const {
    state: { me },
  } = useMeContext()
  const [state] = useServiceDisplayMode()
  const serviceModulePermissions = useServiceModulePermissions()
  const { deleteAttributeFile } = useUploadMutations()

  const [files, setFiles] = React.useState<UploadFile[]>([])
  const [statusChange, setStatusChange] = React.useState<
    'uploadStarted' | 'uploadSuccess' | 'uploadError' | 'fileRemoved' | null
  >()

  React.useEffect(() => {
    setFiles(
      attribute === null
        ? (fileList as UploadFile[])
        : convertValueUploadsToFileList(
            fileList as VersionedValueUpload[],
            attributeFiles || roomAttributeFiles
          )
    )
  }, [attribute, attributeFiles, fileList, roomAttributeFiles])

  const [inited, setInited] = React.useState(false)
  const formItemName = React.useMemo(
    () => name || prepareUploaFormItemId(id),
    [name, id]
  )
  const formFieldName = React.useMemo(
    () => fieldName || formItemName,
    [fieldName, formItemName]
  )
  const setFormFileList = React.useCallback(
    (nextFiles: UploadFile[]) => {
      if (formFieldName) {
        if (typeof form?.setFieldValue === 'function') {
          form.setFieldValue(formFieldName as any, { fileList: nextFiles })
          return
        }

        form?.setFields([
          { name: formFieldName, value: { fileList: nextFiles } },
        ])
      }
    },
    [form, formFieldName]
  )

  const checkUploadLimit = (file: any) => {
    try {
      const uploadLimit = import.meta.env.VITE_UPLOAD_LIMIT_IN_MB
      if (!uploadLimit) return true

      const maxFileSize = Number(uploadLimit) * 1000000
      if (uploadLimit && file.size > maxFileSize) {
        notification.error({
          duration: 3,
          message: t('common.error.fileIsTooBig', {
            maxFileSize: uploadLimit,
          }),
        })
        return false
      }
    } catch (err) {
      console.warn(err)
    }
    return true
  }

  const handleAxiosError = (error: AxiosError): void => {
    const statusCode = error?.response?.status
    const errorMessagesByStatusCode: { [statusCode: string]: string } = {
      '413': 'common.error.fileIsTooBig',
    }

    if (statusCode && String(statusCode) in errorMessagesByStatusCode) {
      notification.error({
        duration: 3,
        message: t(errorMessagesByStatusCode[String(statusCode)]),
      })
      return
    }

    notification.error({
      duration: 3,
      message: error?.toString(),
    })
  }

  const isOffline = () => {
    if (!window.navigator.onLine) {
      notification.error({
        duration: 3,
        message: t('common.error.missingNetworkConnection'),
      })
      return true
    }
    return false
  }

  const beforeUploadHandler = (file: RcFile) => {
    if (!file.name.includes('.')) {
      notification.error({
        duration: 3,
        message: t('common.error.unknownFileType', { fileName: file.name }),
      })
      return Upload.LIST_IGNORE
    }
    if (!checkUploadLimit(file)) return Upload.LIST_IGNORE
    if (isOffline()) return Upload.LIST_IGNORE
    return true
  }

  const uploadHandler = async (
    options: any,
    insertData?: Uploads_Insert_Input
  ) => {
    const {
      onSuccess,
      onError,
      onProgress: defaultOnProgress,
      file,
      fileList,
    } = options

    const fileName = sanitizeFileName(file?.name)
    const uploadUri = `${UPLOAD_URI}/files/upload/${encodeURIComponent(
      fileName
    )}`

    const config = {
      headers: { 'content-type': file.type },
      withCredentials: true,
      onUploadProgress: (event: any) => {
        const uploadObj = {
          name: fileName,
          loading: event.loaded === event.total ? false : true,
          percent: (event.loaded / event.total) * 100,
        }
        defaultOnProgress(uploadObj)
        onProgress && onProgress(uploadObj)
      },
    }

    try {
      const result = await axios.put(uploadUri, file, config)

      const newFileId = parseInt(result?.data?.fileId)
      const newFile: FileAdded = {
        name: fileName,
        ...result?.data,
        fileId: newFileId,
      }

      if (newFileId) {
        onSuccess({
          ...result?.data,
          uid: newFileId,
        })

        setStatusChange('uploadSuccess')

        onComplete && onComplete(newFile)
      }
    } catch (err) {
      setStatusChange('uploadError')
      handleAxiosError(err as AxiosError)
      onError({ err })
    }
  }

  const uploadNewVersionHandler = async (
    options: UploadRequestOption,
    uploadId: string | number
  ) => {
    const uploadFileName = sanitizeFileName((options.file as RcFile).name)
    const uploadVersionFileName = splitFileNameAndExtension(uploadFileName)

    setFiles(currentFiles =>
      currentFiles.map(existingFile =>
        existingFile.uploadId === uploadId
          ? {
              ...existingFile,
              status: 'uploading',
              percent: 0,
            }
          : existingFile
      )
    )

    await uploadHandler({
      ...options,
      onProgress: (event: { percent?: number }) => {
        options.onProgress?.(event)
        setFiles(currentFiles =>
          currentFiles.map(existingFile =>
            existingFile.uploadId === uploadId
              ? {
                  ...existingFile,
                  status: 'uploading',
                  percent: event.percent,
                }
              : existingFile
          )
        )
      },
      onSuccess: (response: any) => {
        options.onSuccess?.(response, undefined)
        const newFileId = response?.uid || response?.fileId
        if (!newFileId) return
        setFiles(currentFiles => {
          const nextFiles = currentFiles.map(existingFile => {
            if (String(existingFile.uploadId) !== String(uploadId)) {
              return existingFile
            }

            const previousVersion: UploadFileVersion = {
              uid: existingFile.uid,
              name: existingFile.name,
              url: existingFile.url,
              size: existingFile.size,
              extension: existingFile.extension,
              uploadedBy: existingFile.uploadedBy,
              createdAt: existingFile.createdAt,
              isNewVersion: existingFile.isNewVersion,
            }

            return {
              ...existingFile,
              uid: String(newFileId),
              name: uploadVersionFileName.name,
              extension: uploadVersionFileName.extension,
              response: {
                ...response,
                uid: newFileId,
                fileId: newFileId,
              },
              uploadId,
              status: 'done',
              percent: undefined,
              isNewVersion: true,
              uploadedBy: me
                ? { id: me.id, firstName: me.firstName, lastName: me.lastName }
                : null,
              createdAt: new Date().toISOString(),
              newVersionFileIds: [
                ...(existingFile.newVersionFileIds || []),
                newFileId,
              ],
              versions: [previousVersion, ...(existingFile.versions || [])],
            }
          })
          setFormFileList(nextFiles)
          onChanged?.(nextFiles)
          return nextFiles
        })
        setStatusChange('uploadSuccess')
      },
      onError: (error: Error) => {
        options.onError?.(error)
        setFiles(currentFiles =>
          currentFiles.map(existingFile =>
            existingFile.uploadId === uploadId
              ? {
                  ...existingFile,
                  status: 'error',
                  percent: undefined,
                }
              : existingFile
          )
        )
      },
    })
  }

  const deleteVersionHandler = (
    uploadId: string | number,
    versionUid: string | number
  ) => {
    onDeleteVersion?.(versionUid)
    setFiles(currentFiles => {
      const nextFiles = currentFiles
        .map(file => {
          if (String(file.uploadId) !== String(uploadId)) return file

          const currentVersion: UploadFileVersion = {
            uid: file.uid,
            name: file.name,
            url: file.url,
            size: file.size,
            extension: file.extension,
            uploadedBy: file.uploadedBy,
            createdAt: file.createdAt,
            isNewVersion: file.isNewVersion,
          }
          const allVersions = [currentVersion, ...(file.versions || [])]
          const versionToDelete = allVersions.find(
            v => String(v.uid) === String(versionUid)
          )
          const remainingVersions = allVersions.filter(
            v => String(v.uid) !== String(versionUid)
          )

          if (!versionToDelete) return file

          const deletedVersions = versionToDelete.isNewVersion
            ? file.deletedVersions || []
            : [
                ...(file.deletedVersions || []),
                { uploadId, fileId: versionToDelete.uid },
              ]

          const [nextCurrent, ...nextPreviousVersions] = remainingVersions
          if (!nextCurrent) return null

          return {
            ...file,
            uid: nextCurrent.uid,
            name: nextCurrent.name,
            url: nextCurrent.url,
            size: nextCurrent.size,
            extension: nextCurrent.extension,
            uploadedBy: nextCurrent.uploadedBy,
            createdAt: nextCurrent.createdAt,
            isNewVersion: nextCurrent.isNewVersion,
            versions: nextPreviousVersions,
            deletedVersions,
            newVersionFileIds: (file.newVersionFileIds || []).filter(
              fileId => String(fileId) !== String(versionUid)
            ),
          }
        })
        .filter(Boolean) as UploadFile[]

      return nextFiles
    })
    setStatusChange('fileRemoved')
  }

  React.useEffect(() => {
    // only update state if a file changes 'uploadStarted' | 'uploadSuccess' | 'uploadError'
    // updateing the contoext on every upload precentage state update creates too many state updates
    if (inited && statusChange) {
      setFormFileList(files)
      onChanged && onChanged(files)
      setStatusChange(null)
    }
  }, [files, inited, onChanged, setFormFileList, statusChange])

  const isExport = state.serviceDisplayMode === ServiceDisplayMode.EXPORT
  const readOnly = state.serviceDisplayMode === ServiceDisplayMode.READ_ACCESS

  React.useEffect(() => {
    setInited(true)
  }, [])

  if (isExport) {
    return (
      <DisplayList>
        {files.map(file => {
          const fileName = formatFileName(file.name, file.extension)

          return (
            file.type !== 'attributeFile' && (
              <DisplayListItem key={file.uid}>
                <div className="fileName">{fileName}</div>
                <a
                  className="url"
                  href={file.url}
                  target="_blank"
                  rel="noopener"
                >{`${document.location.origin}${file.url}`}</a>
              </DisplayListItem>
            )
          )
        })}
      </DisplayList>
    )
  }

  if (readOnly) {
    return (
      <FileList>
        {files.map((file, key) => (
          <React.Fragment key={key}>
            {renderAttributeFileEntry(file)}
          </React.Fragment>
        ))}
      </FileList>
    )
  }

  if (!module) {
    return (
      <UploadWrapper>
        <Form.Item noStyle>
          <Upload
            multiple
            disabled={
              isConfiguration
                ? false
                : !serviceModulePermissions.writable || disableUpload
            }
            fileList={files?.length ? files : undefined}
            onChange={({ fileList: newFileList, file, event }) => {
              if (file.status === 'error') {
                const nextFiles = newFileList.filter(f => f.uid !== file.uid)
                setFiles(nextFiles)
                setFormFileList(nextFiles)
                return
              }
              setFiles(newFileList)
              setFormFileList(newFileList)

              if (
                file.status === 'uploading' &&
                !files.some(f => f.uid === file.uid)
              ) {
                setStatusChange('uploadStarted')
              }
            }}
            onRemove={async file => {
              // TODO: Remove isConfiguration part - Service Config & RoomService Config are different here
              // catch will return true, even if it fails, because RoomService Config needs it
              // files should never be removed directly, but on save. ServiceConfig needs to be updates
              try {
                // Configuration uses deleteAttributeFile, because attributeValue only exist for eventAttirbutes
                if (isConfiguration) {
                  const result = await deleteAttributeFile({
                    variables: {
                      fileId: file.uid,
                      attributeId: attributeId,
                    },
                  })
                  const deletedAttributeFile =
                    result.data?.delete_attributeFiles_by_pk

                  if (deletedAttributeFile) {
                    const nextFiles = files.filter(f => f.uid !== file.uid)
                    setFiles(nextFiles)
                    setFormFileList(nextFiles)
                    setStatusChange('fileRemoved')
                    return true
                  }
                  return false
                }
                const nextFiles = files.filter(f => f.uid !== file.uid)
                setFiles(nextFiles)
                setFormFileList(nextFiles)
                setStatusChange('fileRemoved')
                return true // not really deleting here
              } catch (err) {
                // console.error(err)
                const nextFiles = files.filter(f => f.uid !== file.uid)
                setFiles(nextFiles)
                setFormFileList(nextFiles)
                setStatusChange('fileRemoved')
                return true
              }
              return true
            }}
            beforeUpload={beforeUploadHandler}
            customRequest={options => uploadHandler(options)}
            itemRender={(originNode, file, currFileList, actions) => {
              if (!isConfiguration && file.type === 'attributeFile') {
                return renderAttributeFileEntry(file)
              }
              if (!isConfiguration) {
                return (
                  <RegularFileEntry
                    file={file as UploadFile}
                    actions={actions}
                    isConfiguration={false}
                    beforeUpload={beforeUploadHandler}
                    onUploadNewVersion={
                      onUploadNewVersion || uploadNewVersionHandler
                    }
                    onDeleteVersion={deleteVersionHandler}
                  />
                )
              }
              return (
                <RegularFileEntry
                  file={file as UploadFile}
                  actions={actions}
                  isConfiguration={true}
                  beforeUpload={beforeUploadHandler}
                  onUploadNewVersion={
                    onUploadNewVersion || uploadNewVersionHandler
                  }
                  onDeleteVersion={deleteVersionHandler}
                />
              )
            }}
          >
            {children}
          </Upload>
        </Form.Item>
      </UploadWrapper>
    )
  }

  if (isConfiguration ? false : !serviceModulePermissions.writable) {
    return null
  }

  return (
    <UploadWrapperHideList>
      <Upload
        multiple
        disabled={isConfiguration ? false : !serviceModulePermissions.writable}
        beforeUpload={beforeUploadHandler}
        customRequest={options =>
          uploadHandler(options, {
            serviceModuleId: module.id,
          })
        }
      >
        {children}
      </Upload>
    </UploadWrapperHideList>
  )
}
